import React, { useEffect, useRef, useState } from 'react';
import { ConnectionState } from 'livekit-client';
import { mountNebulaOrb } from './nebulaOrb/mountNebulaOrb';
import './nebulaOrb/nebulaOrb.css';

const BABYLON_SCRIPT_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/babylonjs/6.26.0/babylon.js';

let babylonLoadPromise = null;

function ensureBabylon() {
  if (typeof window !== 'undefined' && window.BABYLON) {
    return Promise.resolve();
  }
  if (!babylonLoadPromise) {
    babylonLoadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${BABYLON_SCRIPT_SRC}"]`);
      if (existing) {
        if (window.BABYLON) resolve();
        else {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('Failed to load Babylon.js')));
        }
        return;
      }
      const s = document.createElement('script');
      s.src = BABYLON_SCRIPT_SRC;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Babylon.js'));
      document.head.appendChild(s);
    });
  }
  return babylonLoadPromise;
}

/**
 * Nebula / LiveKit orb. On the reading page, LiveKit is owned by ReadingSession —
 * pass liveKitRoomRef + liveKitConnectionState so the orb can react to agent audio without a second room.
 *
 * @param {{ liveKitRoomRef?: React.MutableRefObject<import('livekit-client').Room | null>, liveKitConnectionState?: import('livekit-client').ConnectionState }} props
 */
function NebulaOrb({ liveKitRoomRef, liveKitConnectionState } = {}) {
  const rootRef = useRef(null);
  const apiRef = useRef(null);
  const embedReadingSession = Boolean(liveKitRoomRef);
  const [orbMounted, setOrbMounted] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    let cancelled = false;

    (async () => {
      try {
        await ensureBabylon();
        if (cancelled || !rootRef.current) return;
        apiRef.current = mountNebulaOrb(rootRef.current, { embedReadingSession });
        if (!cancelled) setOrbMounted(true);
      } catch (e) {
        console.error('[NebulaOrb]', e);
      }
    })();

    return () => {
      cancelled = true;
      setOrbMounted(false);
      const api = apiRef.current;
      apiRef.current = null;
      if (api) {
        api.dispose().catch((e) => console.error('[NebulaOrb] dispose', e));
      }
    };
  }, [embedReadingSession]);

  useEffect(() => {
    if (!orbMounted || !embedReadingSession) return;
    const api = apiRef.current;
    if (!api?.setLiveKitRoom) return;
    const room = liveKitRoomRef?.current;
    if (liveKitConnectionState === ConnectionState.Connected && room) {
      api.setLiveKitRoom(room);
    } else {
      api.setLiveKitRoom(null);
    }
  }, [orbMounted, embedReadingSession, liveKitConnectionState, liveKitRoomRef]);

  return (
    <div
      ref={rootRef}
      className="nebulaOrbRoot"
      title="Nebula Orb"
      style={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        /* avatar-overlay is flex with alignItems: center — without stretch the orb won’t fill 200px / 400px */
        alignSelf: 'stretch',
      }}
    >
      <div
        className="nebulaOrb_container"
        style={{
          // We removed the sidebar; keep the canvas full width.
          gridTemplateColumns: 'minmax(0, 1fr)',
        }}
      >
        <div className="nebulaOrb_canvasWrap">
          <canvas className="nebulaOrb_renderCanvas" />
        </div>
      </div>
    </div>
  );
}

export default NebulaOrb;
